import {
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';

import { useObscured } from '../hooks/useObscured.js';
import styles from './ChatLayout.module.css';

interface ChatLayoutProps {
  rail?: ReactNode;
  inbox: ReactNode;
  conversation: ReactNode;
  contextPanel?: ReactNode;
  /** Whether a conversation is open. On mobile this decides which single pane
   *  shows (list vs conversation) and whether the bottom rail is visible. */
  hasSelection: boolean;
}

const WIDTH_KEY = 'chat-emp.context-width';
const DEFAULT_WIDTH = 340;
const MIN_WIDTH = 240;
const MAX_WIDTH = 640;
const KEY_STEP = 16;

const clamp = (width: number) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(width)));

function readWidth(): number {
  try {
    const stored = Number(localStorage.getItem(WIDTH_KEY));
    return stored ? clamp(stored) : DEFAULT_WIDTH;
  } catch {
    return DEFAULT_WIDTH;
  }
}

function storeWidth(width: number) {
  try {
    localStorage.setItem(WIDTH_KEY, String(width));
  } catch {
    // Private mode or a full quota: the width then lives as long as the page.
  }
}

export function ChatLayout({
  rail,
  inbox,
  conversation,
  contextPanel,
  hasSelection,
}: ChatLayoutProps) {
  const obscured = useObscured();
  const [width, setWidth] = useState(readWidth);
  const context = useRef<HTMLElement>(null);
  const drag = useRef<{ anchor: number; latest?: number } | null>(null);

  const commit = (next: number) => {
    const clamped = clamp(next);
    setWidth(clamped);
    storeWidth(clamped);
  };
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    // The grid may draw the panel narrower than `width` to keep the thread's
    // floor, so the drag starts from the drawn width, not the stored one.
    drag.current = { anchor: e.clientX + (context.current?.offsetWidth || width) };
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    drag.current.latest = clamp(drag.current.anchor - e.clientX);
    setWidth(drag.current.latest);
  };
  const onPointerUp = () => {
    if (drag.current?.latest !== undefined) storeWidth(drag.current.latest);
    drag.current = null;
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.key === 'ArrowLeft' ? KEY_STEP : e.key === 'ArrowRight' ? -KEY_STEP : 0;
    if (!step) return;
    e.preventDefault();
    commit(width + step);
  };

  return (
    <div
      className={styles.shell}
      data-view={hasSelection ? 'conversation' : 'list'}
      data-obscured={obscured ? '' : undefined}
    >
      {rail}
      <div className={styles.wrap}>
        <div
          className={contextPanel ? styles.cardThree : styles.cardTwo}
          style={{ '--context-width': `${width}px` } as CSSProperties}
        >
          <aside className={styles.inbox}>{inbox}</aside>
          <main className={styles.conversation}>{conversation}</main>
          {contextPanel && (
            <>
              <div
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize details panel"
                aria-valuemin={MIN_WIDTH}
                aria-valuemax={MAX_WIDTH}
                aria-valuenow={width}
                tabIndex={0}
                className={styles.resizer}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onDoubleClick={() => commit(DEFAULT_WIDTH)}
                onKeyDown={onKeyDown}
              />
              <aside ref={context} className={styles.context}>
                {contextPanel}
              </aside>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
